import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Calendar, Sun, Moon, ArrowRight } from 'lucide-react';

const GrindIntentScreen = ({ onSelect, onSkip }) => {
  const [selected, setSelected] = useState(null);

  const handleSelect = (intent) => {
    setSelected(intent);
  };

  const handleContinue = () => {
    if (selected) {
      onSelect(selected);
    }
  };

  const getDateForIntent = (intent) => {
    const today = new Date();
    if (intent === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday.toISOString().split('T')[0];
    }
    return today.toISOString().split('T')[0];
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-lg">
        <Card className="bg-slate-800/90 border-slate-700 backdrop-blur-xl shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Calendar className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              What's Your Grind?
            </CardTitle>
            <CardDescription className="text-slate-300 text-base">
              Choose how you want to start your session
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4 pt-4">
            {/* Today Option - Goes to Regular Dashboard */}
            <button
              onClick={() => handleSelect('today')}
              className={`w-full p-6 rounded-xl border-2 transition-all duration-200 flex items-center gap-4 ${
                selected === 'today'
                  ? 'border-amber-500 bg-amber-500/20 shadow-lg shadow-amber-500/20'
                  : 'border-slate-600 bg-slate-700/50 hover:border-slate-500 hover:bg-slate-700'
              }`}
              data-testid="grind-today-btn"
            >
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                selected === 'today' ? 'bg-amber-500' : 'bg-slate-600'
              }`}>
                <Sun className={`w-7 h-7 ${selected === 'today' ? 'text-white' : 'text-slate-300'}`} />
              </div>
              <div className="text-left flex-1">
                <h3 className={`text-lg font-semibold ${selected === 'today' ? 'text-amber-400' : 'text-white'}`}>
                  Grind Today
                </h3>
                <p className="text-slate-400 text-sm">
                  Track hydration, nutrition, sleep & activities
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </p>
              </div>
              {selected === 'today' && (
                <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>

            {/* Yesterday Option - Goes to Video Review */}
            <button
              onClick={() => handleSelect('yesterday')}
              className={`w-full p-6 rounded-xl border-2 transition-all duration-200 flex items-center gap-4 ${
                selected === 'yesterday'
                  ? 'border-blue-500 bg-blue-500/20 shadow-lg shadow-blue-500/20'
                  : 'border-slate-600 bg-slate-700/50 hover:border-slate-500 hover:bg-slate-700'
              }`}
              data-testid="grind-yesterday-btn"
            >
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                selected === 'yesterday' ? 'bg-blue-500' : 'bg-slate-600'
              }`}>
                <Moon className={`w-7 h-7 ${selected === 'yesterday' ? 'text-white' : 'text-slate-300'}`} />
              </div>
              <div className="text-left flex-1">
                <h3 className={`text-lg font-semibold ${selected === 'yesterday' ? 'text-blue-400' : 'text-white'}`}>
                  Grind from Yesterday
                </h3>
                <p className="text-slate-400 text-sm">
                  Log game stats & video review session
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  {(() => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    return yesterday.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
                  })()}
                </p>
              </div>
              {selected === 'yesterday' && (
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="ghost"
                className="flex-1 text-slate-400 hover:text-white hover:bg-slate-700"
                onClick={onSkip}
                data-testid="skip-grind-intent-btn"
              >
                Skip for now
              </Button>
              <Button
                className={`flex-1 ${
                  selected
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white'
                    : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                }`}
                onClick={handleContinue}
                disabled={!selected}
                data-testid="continue-grind-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GrindIntentScreen;
